import React, { useState, useEffect, useCallback } from "react";
import { useSignUp } from '@clerk/clerk-react';
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2, Check, Fingerprint, User, Phone, Shield } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { useSimpleMode } from "@/contexts/SimpleModeContext";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

// Auth components
import {
  PinInput,
  MobileNumberInput,
  CountryCodeSelector,
  BiometricButton,
  ForgotPinModal,
  CountryCode,
  COUNTRY_CODES,
} from "@/components/auth";

// UI components
import { Button, Card, CardContent, Input, Label } from "@/components/ui";
import { InlineError } from "@/components/interactions";
import { VersionStamp } from "@/components/VersionStamp";

// Assets
import agxlLogo from "@/assets/logo.png";

// ============================================================================
// Types
// ============================================================================

type AuthStep =
  | "welcome"
  | "phone"
  | "pin"
  | "register-phone"
  | "register-otp"
  | "register-name"
  | "register-pin"
  | "register-confirm-pin"
  | "biometric-prompt"
  | "success";

interface FormState {
  phoneNumber: string;
  countryCode: CountryCode;
  pin: string;
  confirmPin: string;
  name: string;
  rememberMe: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const INITIAL_FORM_STATE: FormState = {
  phoneNumber: '',
  countryCode: COUNTRY_CODES[0],
  pin: "",
  confirmPin: "",
  name: "",
  rememberMe: true,
};

// Animation variants
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 300 : -300,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 300 : -300,
    opacity: 0,
  }),
};

// ============================================================================
// Component
// ============================================================================

export const AuthScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const {
    loginWithPin,
    loginWithBiometric,
    register,
    requestOtp,
    verifyOtp,
    resetPin,
    isBiometricAvailable,
    enrollBiometric,
  } = useAuth();
  const { isSimpleMode } = useSimpleMode();

  // Clerk sign-up for phone verification
  const { signUp, isLoaded: clerkLoaded } = useSignUp();
  const [otpCode, setOtpCode] = useState('');
  const [clerkUserId, setClerkUserId] = useState<string | null>(null);
  // INVITE-ONLY U4 (§6.2): the Clerk session the completed SMS step created; farmer-signup checks it with Clerk.
  const [clerkSessionId, setClerkSessionId] = useState<string | null>(null);
  // INVITE-ONLY U4: farmer-signup refusal codes → the farmer's own language (never a raw code).
  const signupRefusal = useCallback((code?: string): string => {
    switch (code) {
      case 'NOT_INVITED':
        return t('auth.notInvited', 'Ask your organisation to add your number');
      case 'PHONE_EXISTS':
        return t('auth.phoneExists', 'This number is already registered. Please log in.');
      case 'COUNTRY_NOT_ENABLED':
        return t('auth.countryNotEnabled', 'AGXL is not available in your country yet.');
      case 'OTP_REQUIRED':
      case 'OTP_UNVERIFIED':
      case 'PHONE_NOT_VERIFIED':
        return t('auth.verifyPhoneAgain', 'Please verify your phone number again with a new code.');
      default:
        return t('auth.registerFailed', 'Registration failed');
    }
  }, [t]);

  // State
  const [step, setStep] = useState<AuthStep>("welcome");
  const [direction, setDirection] = useState(1);
  const [form, setForm] = useState<FormState>(INITIAL_FORM_STATE);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showForgotPin, setShowForgotPin] = useState(false);
  const [biometricError, setBiometricError] = useState("");
  const [attemptsRemaining, setAttemptsRemaining] = useState(3);

  // Get redirect path
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || "/";

  // ============================================================================
  // Navigation Helpers
  // ============================================================================

  const goToStep = useCallback((newStep: AuthStep, dir: number = 1) => {
    setDirection(dir);
    setError("");
    setStep(newStep);
  }, []);

  const goBack = useCallback(() => {
    const backMap: Record<AuthStep, AuthStep> = {
      welcome: "welcome",
      phone: "welcome",
      pin: "phone",
      "register-phone": "welcome",
      "register-otp": "register-phone",
      "register-name": "register-otp",
      "register-pin": "register-name",
      "register-confirm-pin": "register-pin",
      "biometric-prompt": "success",
      success: "success",
    };
    goToStep(backMap[step], -1);
  }, [step, goToStep]);

  // ============================================================================
  // Form Handlers
  // ============================================================================

  const updateForm = useCallback((updates: Partial<FormState>) => {
    setForm((prev) => ({ ...prev, ...updates }));
    setError("");
  }, []);

  // ============================================================================
  // Auth Actions
  // ============================================================================
  // Use ref to prevent multiple concurrent login attempts
  const isLoginInProgress = React.useRef(false);

  const handleLogin = useCallback(
    async (pinOverride?: string) => {
      const pinToUse = pinOverride || form.pin;
      if (pinToUse.length !== 4) return;
      if (isLoginInProgress.current) return;

      isLoginInProgress.current = true;
      setIsLoading(true);
      setError("");

      try {
        const fullPhone = `${form.countryCode.dialCode}${form.phoneNumber}`;
        const result = await loginWithPin(fullPhone, pinToUse, form.rememberMe, form.countryCode.code);

        if (result.success) {
          setIsSuccess(true);

          // Check if should prompt for biometric
          const shouldPromptBiometric =
            isBiometricAvailable &&
            !localStorage.getItem("agxl_biometric_enrolled") &&
            !localStorage.getItem("agxl_biometric_declined");

          setTimeout(() => {
            if (shouldPromptBiometric) {
              goToStep("biometric-prompt");
            } else {
              navigate(from, { replace: true });
            }
          }, 800);
        } else if (result.locked) {
          setError(
            t(
              'auth.accountLocked',
              'Your account is locked due to too many failed attempts. Please try again in 15 minutes.',
            ),
          );
          setAttemptsRemaining(0);
          updateForm({ pin: '' });
        } else if (result.attemptsRemaining !== undefined && result.attemptsRemaining > 0) {
          setError(
            t(
              'auth.attemptsRemaining',
              'Incorrect PIN. {{count}} attempt(s) remaining before your account is locked.',
              { count: result.attemptsRemaining },
            ),
          );
          setAttemptsRemaining(result.attemptsRemaining);
          updateForm({ pin: '' });
        } else {
          setError(
            t('auth.invalidCredentials', 'Invalid phone number or PIN. Please try again.'),
          );
          setAttemptsRemaining((prev) => Math.max(0, prev - 1));
          updateForm({ pin: '' });
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : t("auth.loginFailed", "Login failed");
        setError(errorMessage);
        setAttemptsRemaining((prev) => Math.max(0, prev - 1));
        updateForm({ pin: "" });
      } finally {
        setIsLoading(false);
        isLoginInProgress.current = false;
      }
    },
    [
      form.countryCode.dialCode,
      form.phoneNumber,
      form.pin,
      form.rememberMe,
      loginWithPin,
      isBiometricAvailable,
      navigate,
      from,
      goToStep,
      t,
      updateForm,
    ],
  );

  const handleBiometricLogin = useCallback(async () => {
    setIsLoading(true);
    setBiometricError("");

    try {
      await loginWithBiometric();
      setIsSuccess(true);
      setTimeout(() => {
        navigate(from, { replace: true });
      }, 500);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t("auth.biometricFailed", "Biometric login failed");
      setBiometricError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [loginWithBiometric, navigate, from, t]);

  const handleRegister = useCallback(async (confirmPinOverride?: string) => {
    const confirmPin = confirmPinOverride ?? form.confirmPin;
    if (confirmPin !== form.pin) {
      setError(t("auth.pinMismatch", "PINs don't match"));
      updateForm({ confirmPin: "" });
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      await register({
        phoneNumber: form.phoneNumber,
        countryCode: form.countryCode.dialCode,
        countryIso: form.countryCode.code,
        name: form.name,
        pin: form.pin,
        clerkId: clerkUserId,
        clerkSessionId,
      });

      setIsSuccess(true);

      // Prompt for biometric after registration
      setTimeout(() => {
        if (isBiometricAvailable) {
          goToStep("biometric-prompt");
        } else {
          navigate(from, { replace: true });
        }
      }, 800);
    } catch (err: unknown) {
      // INVITE-ONLY U4: farmer-signup refusals carry a code → translated sentence; anything else as before.
      const code = (err as { code?: string })?.code;
      const errorMessage = code ? signupRefusal(code) : err instanceof Error ? err.message : t("auth.registerFailed", "Registration failed");
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [form, register, isBiometricAvailable, navigate, from, goToStep, t, updateForm, clerkUserId, clerkSessionId, signupRefusal]);

  const handleEnrollBiometric = useCallback(async () => {
    setIsLoading(true);

    try {
      await enrollBiometric();
      navigate(from, { replace: true });
    } catch {
      // Skip biometric if failed
      navigate(from, { replace: true });
    } finally {
      setIsLoading(false);
    }
  }, [enrollBiometric, navigate, from]);

  const handleSkipBiometric = useCallback(() => {
    localStorage.setItem("agxl_biometric_declined", "true");
    navigate(from, { replace: true });
  }, [navigate, from]);

  // Forgot PIN handlers
  const handleForgotPinRequestOtp = useCallback(
    async (phone: string) => {
      return await requestOtp(phone);
    },
    [requestOtp],
  );

  const handleForgotPinVerifyOtp = useCallback(
    async (otp: string) => {
      const fullPhone = `${form.countryCode.dialCode}${form.phoneNumber}`;
      return await verifyOtp(fullPhone, otp);
    },
    [form.countryCode.dialCode, form.phoneNumber, verifyOtp],
  );

  const handleForgotPinReset = useCallback(
    async (newPin: string) => {
      try {
        const fullPhone = `${form.countryCode.dialCode}${form.phoneNumber}`;
        await resetPin(fullPhone, "123456", newPin); // OTP already verified
        return true;
      } catch {
        return false;
      }
    },
    [form.countryCode.dialCode, form.phoneNumber, resetPin],
  );

  // ============================================================================
  // PIN completion handlers
  // ============================================================================

  const handlePinComplete = useCallback(
    (pin: string) => {
      updateForm({ pin });
      // Auto-submit on PIN complete - pass the pin directly to avoid stale closure
      handleLogin(pin);
    },
    [updateForm, handleLogin],
  );

  const handleRegisterPinComplete = useCallback(
    (pin: string) => {
      updateForm({ pin });
      goToStep("register-confirm-pin");
    },
    [updateForm, goToStep],
  );

  const handleConfirmPinComplete = useCallback(
    (pin: string) => {
      updateForm({ confirmPin: pin });
      // Auto-submit on confirm
      setTimeout(() => handleRegister(pin), 100);
    },
    [updateForm, handleRegister],
  );

  // ============================================================================
  // Check for stored biometric
  // ============================================================================

  useEffect(() => {
    const biometricEnrolled = localStorage.getItem("agxl_biometric_enrolled") === "true";
    if (biometricEnrolled && isBiometricAvailable && step === "welcome") {
      // Could auto-trigger biometric here
    }
  }, [isBiometricAvailable, step]);

  // ============================================================================
  // Render Step Content
  // ============================================================================

  const renderWelcome = () => (
    <div className="flex flex-col items-center gap-8 py-8">
      {/* Logo */}
      <motion.img
        src={agxlLogo}
        alt="AGXL"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.1 }}
        className="w-20 h-20 object-contain"
        style={{
          filter: 'brightness(0) saturate(100%) invert(79%) sepia(97%) saturate(1450%) hue-rotate(359deg) brightness(105%) contrast(104%)'
        }}
      />

      {/* Title */}
      <div className="text-center space-y-2">
        <h1 className="text-2xl font-bold">{t("auth.welcome", "Welcome to AGXL")}</h1>
        <p className={cn("text-muted-foreground", isSimpleMode && "simple-hide")}>
          {t("auth.welcomeSubtitle", "Track your farm, build your credit")}
        </p>
      </div>

      {/* Biometric button (if enrolled) */}
      {isBiometricAvailable && localStorage.getItem("agxl_biometric_enrolled") === "true" && (
        <div className="w-full space-y-4">
          <BiometricButton
            type="fingerprint"
            onPress={handleBiometricLogin}
            loading={isLoading}
            error={biometricError}
            size="lg"
          />

          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-border" />
            <span className={cn("text-sm text-muted-foreground", isSimpleMode && "simple-hide")}>
              {t("auth.or", "or")}
            </span>
            <div className="flex-1 h-px bg-border" />
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="w-full space-y-3">
        <Button
          onClick={() => goToStep("phone")}
          className={cn("w-full", isSimpleMode ? "h-20 text-xl" : "h-14 text-lg")}
        >
          <div className="flex items-center justify-between w-full px-2">
            <div className="flex items-center gap-3">
              <Phone className={cn("shrink-0", isSimpleMode ? "w-8 h-8" : "w-5 h-5")} />
              <span>{t("auth.loginWithPhone", "Login with Phone")}</span>
            </div>
            <ArrowRight className={cn("shrink-0", isSimpleMode ? "w-8 h-8" : "w-5 h-5")} />
          </div>
        </Button>

        <Button
          variant="outline"
          onClick={() => goToStep("register-phone")}
          className={cn("w-full", isSimpleMode ? "h-20 text-xl" : "h-14 text-lg")}
        >
          <div className="flex items-center justify-between w-full px-2">
            <div className="flex items-center gap-3">
              <User className={cn("shrink-0", isSimpleMode ? "w-8 h-8" : "w-5 h-5")} />
              <span>{t("auth.createAccount", "Create Account")}</span>
            </div>
            <ArrowRight className={cn("shrink-0", isSimpleMode ? "w-8 h-8" : "w-5 h-5")} />
          </div>
        </Button>
      </div>

      {/* Test credentials hint (dev only) */}
      {import.meta.env.DEV && (
        <div className="text-xs text-muted-foreground/50 text-center space-y-1">
          <p>Test: Any phone ending in 1234567890</p>
          <p>PIN: 1234</p>
        </div>
      )}
    </div>
  );

  const renderPhone = () => (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
          <Phone className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-xl font-semibold">{t("auth.enterPhone", "Enter Your Phone")}</h2>
        <p className={cn("text-sm text-muted-foreground", isSimpleMode && "simple-hide")}>
          {t("auth.enterPhoneSubtitle", "We'll use this to identify your account")}
        </p>
      </div>

      <MobileNumberInput
        value={form.phoneNumber}
        onChange={(val) => updateForm({ phoneNumber: val })}
        countryCode={form.countryCode}
        onCountryChange={(code) => updateForm({ countryCode: code })}
        error={error}
        autoFocus
      />

      <Button
        onClick={() => {
          if (form.phoneNumber.length >= 8) {
            goToStep("pin");
          } else {
            setError(t("auth.invalidPhone", "Please enter a valid phone number"));
          }
        }}
        disabled={form.phoneNumber.length < 8}
        className={cn("w-full", isSimpleMode ? "h-16 text-lg" : "h-12")}
      >
        {t("common.continue", "Continue")}
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </div>
  );

  const renderPin = () => (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
          <Shield className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-xl font-semibold">{t("auth.enterPin", "Enter Your PIN")}</h2>
        <p className={cn("text-sm text-muted-foreground", isSimpleMode && "simple-hide")}>
          {form.countryCode.flag} {form.countryCode.dialCode} {form.phoneNumber}
        </p>
      </div>

      <PinInput
        value={form.pin}
        onChange={(val) => updateForm({ pin: val })}
        onComplete={handlePinComplete}
        error={!!error}
        errorMessage={error}
        disabled={isLoading}
        autoFocus
      />

      {attemptsRemaining < 3 && attemptsRemaining > 0 && (
        <p className="text-center text-sm text-amber-600">
          {t("auth.attemptsRemaining", "{{count}} attempts remaining", { count: attemptsRemaining })}
        </p>
      )}

      {isLoading && (
        <div className="flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      )}

      <Button variant="ghost" onClick={() => setShowForgotPin(true)} className="w-full">
        {t("auth.forgotPin", "Forgot PIN?")}
      </Button>
    </div>
  );

  const renderRegisterPhone = () => (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
          <Phone className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-xl font-semibold">{t("auth.yourPhone", "Your Phone Number")}</h2>
        <p className={cn("text-sm text-muted-foreground", isSimpleMode && "simple-hide")}>
          {t("auth.registerPhoneSubtitle", "This will be your login")}
        </p>
      </div>

      <MobileNumberInput
        value={form.phoneNumber}
        onChange={(val) => updateForm({ phoneNumber: val })}
        countryCode={form.countryCode}
        onCountryChange={(code) => updateForm({ countryCode: code })}
        error={error}
        autoFocus
      />

      <Button
        onClick={async () => {
          if (form.phoneNumber.length < 8) {
            setError(t('auth.invalidPhone', 'Please enter a valid phone number'));
            return;
          }

          setIsLoading(true);
          setError('');

          try {
            const fullPhone = `${form.countryCode.dialCode}${form.phoneNumber.replace(/\s/g, '')}`;

            // Duplicate check
            const { data: existing } = await supabase
              .from('profiles')
              .select('id')
              .eq('auth_phone', fullPhone)
              .maybeSingle();

            if (existing) {
              setError(t('auth.phoneExists', 'This number is already registered. Please log in.'));
              return;
            }

            // INVITE-ONLY U4 (§6.1): before any SMS, ask the server whether an organisation added this number.
            const { data: pre } = await supabase.functions.invoke('farmer-signup', {
              body: { action: 'precheck', phone: fullPhone, country_code: form.countryCode.code },
            });
            if (!pre?.success) {
              setError(signupRefusal(pre?.error_code));
              return;
            }

            // Send OTP via Clerk.
            // BUILD-5D: orphan recovery. If Clerk says the number is taken but the
            // duplicate check above found no profile, it's an orphan from a failed
            // prior attempt; clear the orphaned Clerk user server-side and re-prompt.
            // Fail-closed: any recovery failure rethrows to the catch below.
            try {
              await signUp!.create({ phoneNumber: fullPhone });
            } catch (createErr: unknown) {
              const createCode = (createErr as { errors?: { code?: string }[] })?.errors?.[0]?.code;
              if (createCode === 'form_identifier_exists') {
                const { data: rec } = await supabase.functions.invoke('reconcile-orphan-clerk', {
                  body: { phone: fullPhone },
                });
                if (rec?.orphan_cleared) {
                  setError(t('auth.retryAfterRecover', 'We cleared an incomplete earlier signup. Please tap Continue again.'));
                  return;
                }
              }
              throw createErr;
            }
            await signUp!.preparePhoneNumberVerification({ strategy: 'phone_code' });
            setError('');
            goToStep('register-otp');
          } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : t('auth.otpFailed', 'Could not send verification code');
            setError(msg);
          } finally {
            setIsLoading(false);
          }
        }}
        disabled={form.phoneNumber.length < 8 || isLoading || !clerkLoaded}
        className={cn("w-full", isSimpleMode ? "h-16 text-lg" : "h-12")}
      >
        {isLoading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <>
            {t("common.continue", "Continue")}
            <ArrowRight className="w-5 h-5 ml-2" />
          </>
        )}
      </Button>
    </div>
  );

  const renderRegisterOtp = () => (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
          <Shield className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-xl font-semibold">
          {t('auth.enterCode', 'Enter Verification Code')}
        </h2>
        <p className={cn("text-sm text-muted-foreground", isSimpleMode && "simple-hide")}>
          {t('auth.codeSentTo', 'Code sent to')} {form.countryCode.dialCode}{form.phoneNumber}
        </p>
      </div>

      <PinInput
        value={otpCode}
        onChange={(val) => setOtpCode(val)}
        onComplete={async (val) => {
          setIsLoading(true);
          setError('');
          try {
            const result = await signUp!.attemptPhoneNumberVerification({ code: val });
            if (result.status === 'complete') {
              setClerkUserId(result.createdUserId ?? null);
              setClerkSessionId(result.createdSessionId ?? null);
              setError('');
              goToStep('register-name');
            } else {
              setError(t('auth.invalidCode', 'Invalid code. Please try again.'));
            }
          } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : t('auth.invalidCode', 'Invalid code');
            setError(msg);
          } finally {
            setIsLoading(false);
          }
        }}
        length={6}
        disabled={isLoading}
        autoFocus
      />

      {error && <InlineError message={error} />}

      <Button
        onClick={async () => {
          setIsLoading(true);
          setError('');
          try {
            const result = await signUp!.attemptPhoneNumberVerification({ code: otpCode });
            if (result.status === 'complete') {
              setClerkUserId(result.createdUserId ?? null);
              setClerkSessionId(result.createdSessionId ?? null);
              setError('');
              goToStep('register-name');
            } else {
              setError(t('auth.invalidCode', 'Invalid code. Please try again.'));
            }
          } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : t('auth.invalidCode', 'Invalid code');
            setError(msg);
          } finally {
            setIsLoading(false);
          }
        }}
        disabled={otpCode.length < 6 || isLoading}
        className={cn('w-full', isSimpleMode ? 'h-16 text-lg' : 'h-12')}
      >
        {isLoading
          ? <Loader2 className="w-5 h-5 animate-spin" />
          : <>{t('auth.verify', 'Verify')} <ArrowRight className="w-5 h-5 ml-2" /></>
        }
      </Button>

      <button
        type="button"
        onClick={async () => {
          setError('');
          try {
            await signUp!.preparePhoneNumberVerification({ strategy: 'phone_code' });
          } catch {
            setError(t('auth.resendFailed', 'Could not resend. Please try again.'));
          }
        }}
        className="w-full text-sm text-muted-foreground underline"
      >
        {t('auth.resendCode', 'Resend code')}
      </button>
    </div>
  );

  const renderRegisterName = () => (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
          <User className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-xl font-semibold">{t("auth.yourName", "Your Name")}</h2>
        <p className={cn("text-sm text-muted-foreground", isSimpleMode && "simple-hide")}>
          {t("auth.nameSubtitle", "How should we call you?")}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="name" className={cn(isSimpleMode && "simple-hide")}>
          {t("auth.fullName", "Full Name")}
        </Label>
        <Input
          id="name"
          type="text"
          value={form.name}
          onChange={(e) => updateForm({ name: e.target.value })}
          placeholder={t("auth.namePlaceholder", "Enter your name")}
          className={cn("text-lg", isSimpleMode && "h-16 text-xl")}
          autoFocus
        />
        {error && <InlineError message={error} />}
      </div>

      <Button
        onClick={() => {
          if (form.name.trim().length >= 2) {
            goToStep("register-pin");
          } else {
            setError(t("auth.invalidName", "Please enter your name"));
          }
        }}
        disabled={form.name.trim().length < 2}
        className={cn("w-full", isSimpleMode ? "h-16 text-lg" : "h-12")}
      >
        {t("common.continue", "Continue")}
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </div>
  );

  const renderRegisterPin = () => (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
          <Shield className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-xl font-semibold">{t("auth.createPin", "Create Your PIN")}</h2>
        <p className={cn("text-sm text-muted-foreground", isSimpleMode && "simple-hide")}>
          {t("auth.createPinSubtitle", "Choose a 4-digit PIN you'll remember")}
        </p>
      </div>

      <PinInput
        value={form.pin}
        onChange={(val) => updateForm({ pin: val })}
        onComplete={handleRegisterPinComplete}
        disabled={isLoading}
        autoFocus
      />
    </div>
  );

  const renderRegisterConfirmPin = () => (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
          <Check className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-xl font-semibold">{t("auth.confirmPin", "Confirm Your PIN")}</h2>
        <p className={cn("text-sm text-muted-foreground", isSimpleMode && "simple-hide")}>
          {t("auth.confirmPinSubtitle", "Enter your PIN again to confirm")}
        </p>
      </div>

      <PinInput
        value={form.confirmPin}
        onChange={(val) => updateForm({ confirmPin: val })}
        onComplete={handleConfirmPinComplete}
        error={!!error}
        errorMessage={error}
        disabled={isLoading}
        autoFocus
      />

      {isLoading && (
        <div className="flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      )}
    </div>
  );

  const renderBiometricPrompt = () => (
    <div className="space-y-8 py-8">
      <div className="text-center space-y-2">
        <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
          <Fingerprint className="w-10 h-10 text-primary" />
        </div>
        <h2 className="text-xl font-semibold">{t("auth.enableBiometric", "Enable Quick Login?")}</h2>
        <p className={cn("text-sm text-muted-foreground", isSimpleMode && "simple-hide")}>
          {t("auth.biometricSubtitle", "Use your fingerprint for faster, secure login")}
        </p>
      </div>

      <div className="space-y-3">
        <Button
          onClick={handleEnrollBiometric}
          disabled={isLoading}
          className={cn("w-full gap-2", isSimpleMode ? "h-16 text-lg" : "h-14")}
        >
          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <Fingerprint className="w-5 h-5" />
              <span className={cn(isSimpleMode && "simple-hide")}>
                {t("auth.enableFingerprint", "Enable Fingerprint")}
              </span>
            </>
          )}
        </Button>

        <Button variant="ghost" onClick={handleSkipBiometric} disabled={isLoading} className="w-full">
          {t("common.skip", "Skip for now")}
        </Button>
      </div>
    </div>
  );

  const renderSuccess = () => (
    <div className="flex flex-col items-center justify-center py-16 gap-6">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 200, damping: 15 }}
        className="w-24 h-24 rounded-full bg-green-500/10 flex items-center justify-center"
      >
        <Check className="w-12 h-12 text-green-500" />
      </motion.div>
      <h2 className="text-xl font-semibold text-green-600">{t("auth.success", "Welcome!")}</h2>
    </div>
  );

  // Step renderer map
  const stepRenderers: Record<AuthStep, () => React.ReactNode> = {
    welcome: renderWelcome,
    phone: renderPhone,
    pin: renderPin,
    "register-phone": renderRegisterPhone,
    "register-otp": renderRegisterOtp,
    "register-name": renderRegisterName,
    "register-pin": renderRegisterPin,
    "register-confirm-pin": renderRegisterConfirmPin,
    "biometric-prompt": renderBiometricPrompt,
    success: renderSuccess,
  };

  // ============================================================================
  // Main Render
  // ============================================================================

  const showBackButton = step !== "welcome" && step !== "success" && step !== "biometric-prompt";

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-primary/5 to-background">
      {/* Header */}
      <header className="flex items-center h-14 px-4">
        {showBackButton && (
          <Button variant="ghost" size="icon" onClick={goBack} className="mr-auto">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        )}
      </header>

      {/* Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={step}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ type: "tween", duration: 0.25 }}
              >
                {stepRenderers[step]()}
              </motion.div>
            </AnimatePresence>
          </CardContent>
        </Card>
      </main>

      {/* Forgot PIN Modal */}
      <ForgotPinModal
        open={showForgotPin}
        onOpenChange={setShowForgotPin}
        onRequestOtp={handleForgotPinRequestOtp}
        onVerifyOtp={handleForgotPinVerifyOtp}
        onResetPin={handleForgotPinReset}
        initialPhoneNumber={form.phoneNumber}
        initialCountry={form.countryCode}
      />
      <VersionStamp fixed />
    </div>
  );
};

export default AuthScreen;
